/**
 * EL ACCESO AL ASISTENTE — la parte pura de las pantallas.
 *
 * Usuarios y "Quién le puede hablar" muestran lo mismo con pocas palabras: si
 * alguien tiene acceso, cuánto lo usó y por dónde le habla. Vive acá y no en los
 * componentes porque son decisiones que se pueden probar — sobre todo cuándo se
 * pide confirmación y qué no puede tocar un administrador.
 */

export type RolAcceso = 'dueno' | 'equipo';

/** Lo que se elige en el selector de Usuarios. */
export type OpcionAcceso = 'sin-acceso' | RolAcceso;

export interface AccesoActual {
  rol: RolAcceso;
  activo: boolean;
}

export type CambioDeAcceso =
  | { tipo: 'quitar' }
  | { tipo: 'dar'; rol: RolAcceso; confirmar: boolean }
  | null;

/** Lo que se le dice a quien está por dar acceso de Dueño. */
export const AVISO_CONFIRMAR_DUENO =
  'Como Dueño ve todo, incluida la plata, y puede ver lo que hablan los demás con el asistente.';

const NOMBRE_DE_ROL: Record<RolAcceso, string> = { dueno: 'Dueño', equipo: 'Equipo' };

/** "Sin acceso", "Equipo", "Dueño", o el rol con "· pausado". PURO. */
export function etiquetaDeAcceso(acceso: AccesoActual | null): string {
  if (!acceso) return 'Sin acceso';
  const rol = NOMBRE_DE_ROL[acceso.rol] ?? 'Equipo';
  return acceso.activo ? rol : `${rol} · pausado`;
}

const mensajes = (n: number) => `${n} ${n === 1 ? 'mensaje' : 'mensajes'}`;

/** Para la fila de Usuarios: "12 mensajes · 30 días". PURO. */
export function usoCorto(n: number): string {
  return `${mensajes(n)} · 30 días`;
}

/** Para "Quién le puede hablar": "12 mensajes en 30 días". PURO. */
export function usoLargo(n: number): string {
  return `${mensajes(n)} en 30 días`;
}

/** Por dónde le habla una entrada: con usuario, Panel; con teléfono, WhatsApp. PURO. */
export function canalesDeEntrada(e: { userId: string | null; telefono: string | null }): string[] {
  return [...(e.userId ? ['Panel'] : []), ...(e.telefono ? ['WhatsApp'] : [])];
}

/**
 * Lo que muestra el selector. PURO.
 *
 * Pausado se muestra como "sin acceso": es lo que pasa hoy si esa persona le
 * escribe. La etiqueta de al lado dice que está pausado.
 */
export function opcionActual(acceso: AccesoActual | null): OpcionAcceso {
  if (!acceso || !acceso.activo) return 'sin-acceso';
  return acceso.rol;
}

/**
 * Qué hacer cuando se elige una opción. `null` si no cambia nada. PURO.
 *
 * Dar Dueño pide confirmación SIEMPRE que no lo sea ya: también a quien estaba
 * pausado como dueño, porque reactivarlo es volver a dejarle ver la plata.
 */
export function cambioDeAcceso(acceso: AccesoActual | null, elegida: OpcionAcceso): CambioDeAcceso {
  if (elegida === opcionActual(acceso)) return null;
  if (elegida === 'sin-acceso') return { tipo: 'quitar' };
  return { tipo: 'dar', rol: elegida, confirmar: elegida === 'dueno' };
}

/**
 * Las opciones del selector para este usuario. PURO.
 *
 * Un ADMIN no le puede bajar ni quitar el acceso al OWNER: el server lo rechaza
 * igual, pero una opción que se puede elegir y después falla es la peor forma de
 * enterarse.
 */
export function opcionesDeAcceso(
  miRol: string | undefined,
  rolDelUsuario: string,
): { value: OpcionAcceso; label: string; disabled?: boolean }[] {
  const protegido = rolDelUsuario === 'OWNER' && miRol !== 'OWNER';
  return [
    { value: 'sin-acceso', label: 'Sin acceso', disabled: protegido },
    { value: 'equipo', label: 'Equipo', disabled: protegido },
    { value: 'dueno', label: 'Dueño' },
  ];
}

/** El comienzo del aviso que manda el server a quien no tiene acceso. */
const COMIENZO_SIN_ACCESO = 'No tienes acceso al asistente.';

/**
 * ¿La respuesta es el aviso de "sin acceso"? PURO.
 *
 * Los atajos de preguntas no sirven de nada a quien no puede preguntar: la
 * ventanita los esconde mientras la última respuesta sea este aviso.
 */
export function esAvisoSinAcceso(texto: string): boolean {
  return texto.trim().startsWith(COMIENZO_SIN_ACCESO);
}
